// bootPrepare — boot step 1 of 3, SYNCHRONOUS, before the shell mounts.
//
// ONE boot sequence for both builds (design approved 2026-10-07):
//   • parcel: bootShell (the parcel composer) runs bootPrepare → bootResolve →
//     create() → bootApply, reaching the three through the functions registry
//     its caller hands in (FA001: bootShell imports none of them);
//   • published (mermaid runs Smbls.create(app, context), never index.js): the
//     shell's boot plugin (packages/workspace/context.js) runs bootPrepare in
//     its beforeCreate — before the first element exists — then bootResolve;
//     the root's onCreate runs bootApply when `context.bootReady` settles.
//
// This step: the deployment's API base (context.workspaceApiUrl, resolved at
// RUN time from the injected identity — a module-scope value publishes as
// ""), the early language from storage, the polyglot config every mount
// needs, the SDK + its 'sdk' fetch adapter, and `context.methods.sdk` (el.sdk).
//
// Inputs: the context, { sdk, publicSurface, warmSnapshot, forceSdk }.
// Returns { sdk, lang, themeMode, storage, cachedWsLanguage, poly }.
// `cachedWsLanguage` is the warm row's language pair, or, for a row with none,
// the loader's language as a pair that is never enforced (`loaderLang`, below).
import { registerSdkAdapter } from './sdkAdapter.js'
import { getSDK } from './sdk.js'
import { workspaceApiBase } from './workspaceApiBase.js'
import { readStoredThemeMode } from './themeMode.js'
import { bootTrail } from './bootTrail.js'
import {
  DEFAULT_LANG,
  PLATFORM_LANGUAGES,
  enforcedWorkspaceLang,
  langDir,
  resolveLang
} from './languages.js'
// The canonical language registry + the i18n bundle loader. This step is the
// ONE place that seeds `context.polyglot` for every app, before create()
// (parcel) or before the first element (published): smbls's createDomql reads
// `context.polyglot` to auto-register the polyglot plugin and its functions.
import { i18nStaticTranslations } from './i18nLoader.js'

export function bootPrepare(
  context,
  { sdk = null, publicSurface = false, warmSnapshot = null, forceSdk = false } = {}
) {
  if (!context) return null
  // The API base is the DEPLOYMENT's identity, read when the page runs
  // (workspaceApiBase: host rule → injected channel → fail closed).
  if (!context.workspaceApiUrl) context.workspaceApiUrl = workspaceApiBase()

  // 1. Early lang/theme from localStorage. `themeMode` is the user's stored
  // preference (`auto` | `light` | `dark`); keeping `auto` intact lets scratch
  // attach its prefers-color-scheme listener instead of freezing the current
  // OS value at boot.
  const storage = typeof localStorage !== 'undefined' ? localStorage : null
  // FIRST PAINT, synchronous, before auth: this device's stored choice, or
  // English. `localStorage.lang` is VALIDATED against the shipped set — it is
  // written by `setLang` alone, but an old build could have left `de`/`fr`/`es`
  // there, and an unshipped code would strand the session in a language whose
  // bundles do not exist (every key falls through, nothing says why). The
  // full user > device > workspace > English resolution runs at step 7b, once
  // the user and workspace scopes are actually known.
  // First paint knows no workspace yet, so it has exactly one signal: on a
  // branded host the entry has already seeded `whitelabelBrand` (its 600ms
  // race won), and the org's `branding.lang` is known HERE — first paint is
  // Georgian on a `ka` host instead of English until a later step. When the
  // seed lost, `whitelabelBrand` is null and first paint is English.
  //
  // `localStorage.lang` is NOT read any more (Nika 2026-09-10: "default
  // language is english. only workspaces that are specifically set as
  // Georgian should render in georgian"): a device's stored choice used to
  // paint every workspace it had never been chosen for — see resolveLang.
  //
  // The ONE workspace signal first paint can have: the workspace's own
  // language pair (`{ language, languagePolicy }`), cached in the warm boot
  // snapshot by loadPrivateData from the FULL record of the last settled boot
  // (packages/workspace/functions/bootSnapshot.js). `warmSnapshot` is already
  // validated by the caller for THIS user + workspace + URL scope. Without it
  // the warm mount painted English and 8b / applyWorkspaceLangPolicy then
  // painted the workspace language — measured on my.symbols.app 2026-09-23:
  // the tab read "Home" on the splash, then "მთავარი". Resolved through the
  // SAME `resolveLang` + `enforcedWorkspaceLang` pair as step 7b and the
  // policy, so the three cannot disagree. A public surface binds no workspace
  // rule (see the /login note in 7b): it paints exactly as before. A row with
  // no pair: the loader's language, below.
  const _snapWsLanguage =
    warmSnapshot &&
    !publicSurface &&
    warmSnapshot.workspaceLanguage &&
    typeof warmSnapshot.workspaceLanguage === 'object'
      ? warmSnapshot.workspaceLanguage
      : null
  // A warm row with NO pair (written before the pair existed, or from a
  // record with no `settings`) painted English under the loader's Georgian,
  // and step 7b turned it Georgian later (aa33, dev, 2026-10-30, a `ka`
  // session on /user/<slug>: `<html lang>` en at 856 ms, the tab "Profile" at
  // 1494 ms, "პროფილი" at 2622 ms). Such a row now paints the language the
  // pre-bundle loader painted for THIS arrival's workspace scope: the
  // `wsBootLang:<host>` entry, keyed per host AND per scope, which the
  // workspace entry hands in on the row as `loaderLang` (packages/workspace/
  // functions/bootLang.js withBootLangHint). It is the language the scope
  // RESOLVED to last time, the brand level included, so it ranks where the
  // workspace's own language does. Never enforced. A row WITH a pair keeps
  // the pair, even an older one. A shipped code only.
  const _loaderCode =
    warmSnapshot && !publicSurface && !_snapWsLanguage ? warmSnapshot.loaderLang : null
  const _loaderLang = PLATFORM_LANGUAGES.some((l) => l.code === _loaderCode) ? _loaderCode : null
  // Step 7b falls back to this pair when the live record has no `settings`
  // for the same workspace (bootResolve.js): it keeps the loader's language
  // too, rather than English between two Georgian paints.
  const _cachedWsLanguage =
    _snapWsLanguage || (_loaderLang ? { language: _loaderLang, languagePolicy: null } : null)
  const lang = resolveLang({
    workspaceEnforced: enforcedWorkspaceLang(_cachedWsLanguage),
    workspaceDefault: _cachedWsLanguage?.language || null,
    brandDefault: context.state?.whitelabelBrand?.lang || null
  })
  let themeMode = readStoredThemeMode(storage)
  // NO THEME FLASH: the stored choice is the first paint, in both builds.
  // create() reads `context.themeStorageKey` (config.js) for the same key;
  // this write covers a host page whose own inline script did not (and a
  // parcel boot, whose first frame precedes create()). 'auto' keeps whatever
  // the page resolved from the OS. bootApply only corrects it later, to a
  // tenant default the person never overrode.
  if (!context.themeStorageKey) context.themeStorageKey = 'themeMode'
  if (
    (themeMode === 'light' || themeMode === 'dark') &&
    typeof document !== 'undefined' &&
    document.documentElement &&
    typeof document.documentElement.setAttribute === 'function'
  ) {
    document.documentElement.setAttribute('data-theme', themeMode)
  }

  // WCAG 3.1.1 — `<html lang="...">` is required for screen readers.
  // Set as early as possible so any pre-render announce uses the right
  // pronunciation. Re-applied later when `state.lang` changes via the
  // context language-watch effect. `dir` rides with it: the two must never
  // disagree, and an RTL language set without it lays the whole shell out
  // backwards-correct but text-wrong.
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lang
    document.documentElement.dir = langDir(lang)
  }

  if (context.state) {
    context.state.lang = lang

    // Ensure auxiliary state fields that shared helpers depend on are
    // initialized. openNotification pushes into `notifications`; guard
    // in case the standalone state factory didn't include the field.
    if (!Array.isArray(context.state.notifications)) {
      context.state.notifications = []
    }
  }

  // 1b. POLYGLOT — MINTED ONCE, BEFORE ANY MOUNT.
  //
  // `context.polyglot` is what smbls's `createDomql` reads to auto-register the
  // polyglot plugin and its functions (`polyglot`, `getLang`, `setLang`, …). It
  // must exist before the FIRST `create()` call, and there are TWO of them
  // below: the warm-start early mount (from a cached boot snapshot, a few
  // hundred lines down) and the cold mount in step 8.
  //
  // This block used to live inside step 7b — i.e. AFTER the warm mount. On that
  // path `context.polyglot` was still undefined when the shell mounted, so
  // `mergeSharedLibraries` handed the live element the shared library's own
  // polyglot object, and 7b then minted a SECOND object and loaded Georgian
  // into it. The running app never held the object the translations landed in:
  // `context.polyglot.translations.ka` read 0 keys, the interface stayed
  // English, and `<html lang>` was correct because line ~2161 writes the
  // DOCUMENT, which is global. Minting here is what makes both mount paths
  // deliver the same object. Two polyglot objects was the underlying bug.
  //
  // `mergeOneLibrary` (smbls/packages/utils/sharedLibraries.js) fills only the
  // keys a consumer does NOT define, so every field declared here must match
  // what `@symbo-ls/workspace-shared` declares in its own `context.js` — the
  // pinning test is `__tests__/i18nBootLanguage.test.js`.
  //
  // FA204 / frank trap — `BOOT_NAMESPACES` is an INLINED local, not a read of a
  // module-scope constant, for the same reason `setLang.js` inlines `SUPPORTED`
  // and `languages.js` inlines its own lists: `functions/` is a frank section
  // barrel, so a module-scope binding read from inside an exported function is
  // unresolvable in the revived runtime.
  const BOOT_NAMESPACES = [
    'common',
    'auth',
    'account',
    'settings',
    // Added with the shared-package extraction (2026-09-02). Every one of
    // these has real en+ka bundles on disk; a namespace listed here but not
    // shipped is dropped by the loader's own intersection, and one SHIPPED but
    // not listed here resolves English only — the switch simply never reaches
    // it, silently, because English is bundled.
    'shell',
    'editor',
    'members',
    'learn',
    'ai',
    'publish',
    'apps',
    'workspace',
    'admin',
    'data',
    // Store copy is SHELL CHROME (2026-09-03). Every installed app's navbar tab
    // and launcher tile resolves its label through the one store-copy resolver
    // (`functions/storeItemText.js` → `marketplace.app.<slug>.name`), so an app
    // reads the same name here as in the store it came from. That chrome paints
    // on the FIRST route, before the per-route widening in step 7b runs, so the
    // shell manifest's own `i18nNamespaces` entry is not enough on its own — an
    // undeclared namespace here means a Georgian boot flashes (or keeps) the
    // app manifest's English literal.
    'marketplace'
  ]
  if (!context.polyglot) {
    context.polyglot = {
      defaultLang: DEFAULT_LANG,
      languages: PLATFORM_LANGUAGES.map((l) => l.code),
      // Polyglot's own default is `smbls_lang`. The platform key is `lang`, and
      // `initPolyglot` reads this key on render to decide the active language —
      // left at the default it would read an unset key, fall back to
      // `defaultLang`, and `root.update({ lang: 'en' })` straight over whatever
      // was just resolved.
      storageLangKey: 'lang',
      namespaces: BOOT_NAMESPACES.slice(),
      translations: {}
    }
  }
  const _poly = context.polyglot
  if (!_poly.translations) _poly.translations = {}
  if (!_poly.storageLangKey) _poly.storageLangKey = 'lang'
  // The BASELINE load list. Step 7b widens it per route from the running app's
  // manifest; `setLang` re-loads exactly the resulting set on a switch. It has
  // to be non-empty HERE, on both mount paths, or a cold boot would hand 7b an
  // empty set and load `common` alone — which is how a correctly-resolved
  // Georgian boot still rendered English on a page with almost nothing in
  // `common` (50 keys) to translate.
  if (!Array.isArray(_poly.namespaces) || !_poly.namespaces.length) {
    _poly.namespaces = BOOT_NAMESPACES.slice()
  }
  // ENGLISH IS BUNDLED; EVERY OTHER LANGUAGE IS A LAZY, PER-NAMESPACE CHUNK —
  // `functions/i18nLoader.js` owns the split and the measurements behind it.
  //
  // `deepDefaults`-style fill, not a replace: whatever already sits in
  // `translations` (the library's own copy, or a frank payload's serialized
  // one) stays, and a language it lacks is added. It has to run HERE rather
  // than in 7b because `prepareState` seeds the active language's keys into the
  // root state DURING the mount — an empty `translations` at the warm mount is
  // a root state with no seeded strings at all.
  try {
    const _bootStaticTranslations = i18nStaticTranslations()
    for (const code of Object.keys(_bootStaticTranslations)) {
      const existing = _poly.translations[code]
      _poly.translations[code] =
        existing && Object.keys(existing).length
          ? { ..._bootStaticTranslations[code], ...existing }
          : _bootStaticTranslations[code]
    }
  } catch (err) {
    console.warn('[bootShell] static i18n seed failed:', err?.message || err)
  }

  // `themeMode` / `themeResolved` are seeded further down, AFTER the SDK
  // exists — see "WS-THEME-BOOT-WRONG-MODE-1" below the adapter registration.

  // Pattern B: auto-SDK instantiation.
  // Fires when no explicit `sdk` is passed AND either:
  //   (a) isSingleApp — standalone wrapper (one-entry apps map, no pre-built context), OR
  //   (b) the resolved context carries workspaceApiUrl — multi-app shell that
  //       explicitly opts into SDK wiring via its context.js export.
  //
  // This covers the workspace shell (workspace/packages/workspace/) which passes
  // 5 apps + a pre-built context, so isSingleApp=false, but still needs the SDK
  // to authenticate, fetch scopes, and expose el.sdk to sub-app components.
  // The multi-app explicit-sdk path is preserved: pass `sdk` param explicitly to
  // skip this block entirely (e.g. tests, custom shells).
  const needsSdkInit = forceSdk || (!sdk && context?.workspaceApiUrl)
  if (!sdk && needsSdkInit) {
    try {
      sdk = getSDK()
      bootTrail('bootShell:sdk')
    } catch (err) {
      console.warn('[bootShell] SDK init failed:', err?.message || err)
    }
  }

  // 4. Register sdk fetch adapter (proxy passes calls through; safe pre-init).
  if (sdk) {
    registerSdkAdapter({ sdk })
    // el.sdk for every element: DOMQL builds each context's element prototype
    // from `context.methods` once, at the FIRST element. Both boots run this
    // step before that (parcel: before create(); published: the boot
    // plugin's beforeCreate). Every SDK method awaits the SDK's own init
    // (getSDK's init-await proxy), so exposing it before init is safe for
    // calls; the four synchronous reads (getAuthToken / isReady / getToken /
    // logout) are read by code that runs after `context.bootReady`.
    if (!context.methods || typeof context.methods !== 'object') context.methods = {}
    context.methods.sdk = sdk
  }


  return {
    sdk,
    lang,
    themeMode,
    storage,
    cachedWsLanguage: _cachedWsLanguage,
    poly: _poly
  }
}
